import React, { useState, useEffect } from 'react';
import { Dropdown, Menu } from 'antd';
import { useSelector } from 'react-redux';
import {
    PlusOutlined,
    RightOutlined,
    PictureOutlined,
    TableOutlined,
    BarChartOutlined,
    BorderHorizontalOutlined,
} from '@ant-design/icons';
import { INSERT_PAGE_BREAK } from '../../lexical/plugins/PageBreakPlugin';
import { InsertImageDialog } from '../../lexical/plugins/ImagesPlugin';
import { InsertTableDialog } from '../../lexical/plugins/TablePlugin';
import { InsertChartDialog } from '../../lexical/plugins/ChartPlugin';
import useModal from '../../lexical/hooks/useModal';
import { useSignatureModal } from '../../lexical/LexicalDocumentSignature/SignatureModalHook';
import LexicalSignatureModal from '../../lexical/LexicalDocumentSignature/InsertSignatureUsersModal/signatureModal';
import signatureIcon from '../../../assets/CustomButton-Icons/signature-Icon.png';

// Renders a menu row matching the shared `.menu-dropdown` look (icon | text).
const menuItem = (icon, text) => (
    <span className="menu-dropdown-item">
        <span className="menu-dropdown-item-left">{icon}</span>
        <span className="menu-dropdown-item-text">{text}</span>
    </span>
);

const InsertMenu = () => {
    const editor = useSelector((state) => state.editor.lexicalEditor);
    const isReadOnly = useSelector((state) => state.editor.isReadOnly);
    const [insertMenuVisible, setInsertMenuVisible] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [modal, showModal] = useModal();
    const {
        isSignatureModalVisible,
        showSignatureModal,
        hideSignatureModal,
    } = useSignatureModal();

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleMenuClick = ({ key }) => {
        if (!editor) {
            setInsertMenuVisible(false);
            return;
        }

        switch (key) {
            case 'page-break':
                editor.dispatchCommand(INSERT_PAGE_BREAK, undefined);
                break;
            case 'image':
                showModal('Insert Image', (onClose) => (
                    <InsertImageDialog activeEditor={editor} onClose={onClose} />
                ));
                break;
            case 'table':
                showModal('Insert Table', (onClose) => (
                    <InsertTableDialog activeEditor={editor} onClose={onClose} />
                ));
                break;
            case 'chart':
                showModal('Insert Chart', (onClose) => (
                    <InsertChartDialog activeEditor={editor} onClose={onClose} />
                ));
                break;
            case 'signature':
                showSignatureModal();
                break;
            default:
                break;
        }

        setInsertMenuVisible(false);
    };

    const insertMenu = (
        <Menu
            onClick={handleMenuClick}
            className="menu-dropdown"
            selectable={false}
        >
            <Menu.Item key="page-break">
                {menuItem(<BorderHorizontalOutlined />, 'Page Break')}
            </Menu.Item>
            <Menu.Item key="image">
                {menuItem(<PictureOutlined />, 'Image')}
            </Menu.Item>
            <Menu.Item key="table">
                {menuItem(<TableOutlined />, 'Table')}
            </Menu.Item>
            <Menu.Item key="chart">
                {menuItem(<BarChartOutlined />, 'Chart')}
            </Menu.Item>
            <Menu.Item key="signature">
                {menuItem(
                    <img
                        src={signatureIcon}
                        alt=""
                        style={{ width: 14, height: 14, objectFit: 'contain' }}
                    />,
                    'Signature',
                )}
            </Menu.Item>
        </Menu>
    );

    return (
        <>
            <Dropdown
                overlay={insertMenu}
                trigger={['click']}
                visible={insertMenuVisible}
                onVisibleChange={setInsertMenuVisible}
                disabled={isReadOnly || !editor}
                placement={isMobile ? 'rightTop' : undefined}
            >
                <div
                    className="navTabItem"
                    role="menuitem"
                    tabIndex={0}
                    onMouseDown={(e) => e.preventDefault()}
                >
                    <span className="nav-tab-trigger">
                        <span className="nav-tab-icon"><PlusOutlined /></span>
                        <span className="nav-tab-label">Insert</span>
                        <span className="nav-tab-arrow"><RightOutlined /></span>
                    </span>
                </div>
            </Dropdown>

            {modal}

            <LexicalSignatureModal
                visible={isSignatureModalVisible}
                onClose={hideSignatureModal}
            />
        </>
    );
};

export default InsertMenu;
